import React, { useState, useEffect, useMemo, useRef, useContext, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import MetadataToolbar from './MetadataToolbar';
import PageHeader from 'Utils/PageHeader';
import { BiSitemap } from 'react-icons/bi';
import SchemaIdentityList from './SchemaIdentityList';
import SchemaIdentityDetail from './SchemaIdentityDetail';
import MetaLandscape from './MetaLandscape';
import RunProfileModal from './RunProfileModal';
import AppContext from 'Utils/AppContext';
import { STATUS } from 'Utils/styleTokens';
import { getDataSourceNumericId } from '../DataSources/datasourcesApi';
import { getOverview } from './metaAnalysisApi';
import { listProfileJobs, startProfileJob, cancelProfileJob, getProfileJob } from './profileApi';
import { ensureConnectionTested } from 'Utils/connectionGate';
import './styles.css';

const THRESHOLD_DEBOUNCE_MS = 300;
const JOB_POLL_INTERVAL_MS = 5000;
const ACTIVE_JOB_STATUSES = new Set(['PENDING', 'RUNNING']);

const Metadata = () => {
  const params = useParams();
  const history = useHistory();
  const { setNotification, isReadOnly } = useContext(AppContext);
  const selectedId = params.identityId || null;

  const [identities, setIdentities] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Default to most-recently-profiled. Risk order buried a just-profiled source wherever
  // its risk tier happened to fall, so the run you just finished could land last.
  const [sort, setSort] = useState('recent');
  const [threshold, setThreshold] = useState(85);
  const [runProfileOpen, setRunProfileOpen] = useState(false);
  // Bumped when a profile job completes so the overview re-fetches with the new snapshot.
  const [overviewNonce, setOverviewNonce] = useState(0);
  // Profile jobs currently PENDING/RUNNING — rendered as status cards at the top of the
  // left-hand list (the Jobs drawer is gone).
  const [activeJobs, setActiveJobs] = useState([]);
  const [cancelling, setCancelling] = useState({});
  const debounceRef = useRef(null);
  const jobPollRef = useRef(null);
  const activeJobIdsRef = useRef(new Set());
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (jobPollRef.current) clearInterval(jobPollRef.current);
    };
  }, []);

  // Re-fetch the overview whenever the similarity threshold changes — cluster
  // membership depends on the threshold, so it's a server-side concern, not a
  // client-side filter. Debounce the slider so dragging doesn't flood the API.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      let cancelled = false;
      setLoading(true);
      setError(null);
      getOverview(threshold)
        .then((data) => {
          if (cancelled) return;
          setIdentities(Array.isArray(data) ? data : []);
        })
        .catch((e) => {
          if (cancelled) return;
          setError(e && e.message ? e.message : 'Failed to load schema identities');
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
      return () => {
        cancelled = true;
      };
    }, THRESHOLD_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [threshold, overviewNonce]);

  // One poll pass over the profile-job list: keep the active ones for the left-hand
  // status cards, and when a previously-active job reaches a terminal state, notify
  // and re-fetch the overview so the fresh snapshot shows up without a manual reload.
  const pollJobs = useCallback(async () => {
    let jobs = [];
    try {
      const page = await listProfileJobs({ page: 0, size: 50 });
      jobs = (page && page.content) || (Array.isArray(page) ? page : []);
    } catch (e) {
      return; // transient — next poll retries
    }
    if (!mountedRef.current) return;
    const active = jobs.filter((j) => ACTIVE_JOB_STATUSES.has(j.status));
    // The list's workItems counters are only as fresh as the platform's own worker poll
    // (30s). The single-job endpoint LIVE-SYNCS an active job, so overlay its counters —
    // that's what makes the running row's progress bar move.
    await Promise.all(
      active.map(async (j) => {
        try {
          const live = await getProfileJob(j.id);
          if (live) {
            if (live.workItemsCompleted != null) j.workItemsCompleted = live.workItemsCompleted;
            if (live.workItemsTotal != null) j.workItemsTotal = live.workItemsTotal;
            if (live.workItemKind != null) j.workItemKind = live.workItemKind;
          }
        } catch (e) {
          // Stale counters still render; next poll retries.
        }
      })
    );
    if (!mountedRef.current) return;
    const activeIds = new Set(active.map((j) => j.id));
    const finished = jobs.filter(
      (j) => activeJobIdsRef.current.has(j.id) && !activeIds.has(j.id)
    );
    activeJobIdsRef.current = activeIds;
    setActiveJobs(active);
    if (finished.length > 0) {
      finished.forEach((j) => {
        const name = j.dataSourceName || `data source ${j.dataSourceId}`;
        setNotification({
          header: j.status === 'SUCCEEDED' ? 'Fingerprint complete' : `Fingerprint ${String(j.status || '').toLowerCase()}`,
          body: (
            <span>
              Fingerprint of {name} {j.status === 'SUCCEEDED' ? 'finished' : `ended: ${j.status}`}.
            </span>
          ),
        });
      });
      setOverviewNonce((n) => n + 1);
    }
  }, [setNotification]);

  // Poll every 5s only while something is active; a first pass on mount picks up
  // runs started elsewhere (e.g. the data source editor).
  useEffect(() => {
    pollJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const anyActive = activeJobs.length > 0;
    if (anyActive && !jobPollRef.current) {
      jobPollRef.current = setInterval(pollJobs, JOB_POLL_INTERVAL_MS);
    }
    if (!anyActive && jobPollRef.current) {
      clearInterval(jobPollRef.current);
      jobPollRef.current = null;
    }
  }, [activeJobs, pollJobs]);

  const selectedIdentity = useMemo(() => {
    if (!identities || !selectedId) return null;
    return identities.find((d) => String(d.id) === String(selectedId)) || null;
  }, [identities, selectedId]);

  // An identity's id is its representative snapshot's schema hash, so a re-profile
  // that changes the schema mints a NEW id. Remember the selected identity's member
  // sources and, when its id vanishes on a reload, follow the selection to the
  // identity that now contains those members — the panel updates in place instead
  // of appearing as a fresh unselected card.
  const lastSelectionRef = useRef(null);
  useEffect(() => {
    if (selectedIdentity) {
      lastSelectionRef.current = {
        id: String(selectedIdentity.id),
        memberIds: (selectedIdentity.dataSources || []).map((d) => String(d.dataSourceId)),
      };
    }
  }, [selectedIdentity]);
  useEffect(() => {
    if (!identities || !selectedId || selectedIdentity) return;
    const last = lastSelectionRef.current;
    if (!last || last.id !== String(selectedId)) return;
    const successor = identities.find((identity) =>
      (identity.dataSources || []).some((d) => last.memberIds.includes(String(d.dataSourceId)))
    );
    if (successor) {
      history.replace(`/metadata/${encodeURIComponent(String(successor.id))}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identities, selectedId, selectedIdentity]);

  const handleSelect = (id) => {
    history.push(`/metadata/${encodeURIComponent(String(id))}`);
  };

  // Optimistically surface a just-started profile as an active job so its running-row
  // appears immediately (no wait for the 5s poll). The next poll reconciles real status;
  // seeding activeJobIdsRef keeps the completion notification firing if it finishes first.
  const handleProfileStarted = useCallback((job) => {
    if (!job || job.id == null) return;
    activeJobIdsRef.current.add(job.id);
    setActiveJobs((prev) =>
      prev.some((j) => String(j.id) === String(job.id)) ? prev : [job, ...prev]
    );
  }, []);

  const handleProfileSuccess = (job) => {
    setNotification({
      header: 'Fingerprint started',
      body: (
        <span>
          Fingerprint run started (job {job && job.id ? job.id : ''}). Results will appear when it completes.
        </span>
      ),
    });
    pollJobs();
  };

  // Actions → Refresh metadata: re-run the profile for every member source of the
  // selected identity. The queued jobs surface in the left-hand list and the overview
  // reloads as each completes.
  const handleRefresh = async () => {
    if (!selectedIdentity) return;
    const ids = (selectedIdentity.dataSources || [])
      .map((d) => getDataSourceNumericId({ id: d.dataSourceId }))
      .filter((id) => id != null);
    if (ids.length === 0) {
      setNotification({
        header: 'Nothing to refresh',
        body: <span>No platform data sources found in this identity.</span>,
      });
      return;
    }
    // Bulk pre-run gate: each member source needs a successful connection test (a fresh
    // one is run when none is on record). Failing sources are skipped rather than routing
    // the user away mid-bulk — the count in the notification points them at the problem.
    const gates = await Promise.all(
      ids.map((sourceId) => ensureConnectionTested(sourceId, { navigateOnFail: false }))
    );
    const runnable = ids.filter((_, i) => gates[i].ok);
    const untestable = ids.length - runnable.length;
    if (runnable.length === 0) {
      setNotification({
        header: 'Nothing to refresh',
        body: <span>Connection tests failed for all sources in this identity. Fix the connections and try again.</span>,
      });
      return;
    }
    const results = await Promise.allSettled(runnable.map((id) => startProfileJob(id)));
    const started = results.filter((r) => r.status === 'fulfilled').length;
    const failed = results.length - started;
    setNotification({
      header: 'Refreshing metadata',
      body: (
        <span>
          Re-running {started} fingerprint{started === 1 ? '' : 's'}
          {failed > 0 ? ` (${failed} failed to start)` : ''}
          {untestable > 0 ? ` (${untestable} skipped — connection test failed)` : ''}.
        </span>
      ),
    });
    pollJobs();
  };

  const handleCancelJob = async (jobId) => {
    setCancelling((prev) => ({ ...prev, [jobId]: true }));
    try {
      await cancelProfileJob(jobId);
      setNotification({
        header: 'Cancel requested',
        body: <span>Cancellation requested for job {jobId}. It will stop on the worker's next poll.</span>,
      });
      pollJobs();
    } catch (e) {
      setNotification({
        header: 'Cancel failed',
        body: <span>{(e && e.message) || 'Could not cancel the job.'}</span>,
      });
    } finally {
      setCancelling((prev) => {
        const next = { ...prev };
        delete next[jobId];
        return next;
      });
    }
  };

  return (
    <div className='metadata-page'>
      <div className='px-3'>
        <PageHeader
          header='Schema Intelligence'
          icon={BiSitemap}
          help='Fingerprint scans read schema and table metadata (no data is sampled) and group databases whose schemas match into identity clusters, matched against known commercial products.'
          tooltip='Cross-source schema identities and lineage'
        />
      </div>
      <MetadataToolbar
        threshold={threshold}
        onThresholdChange={setThreshold}
        sort={sort}
        onSortChange={setSort}
        onNewProfile={() => setRunProfileOpen(true)}
        onRefresh={handleRefresh}
        refreshEnabled={!!selectedIdentity}
      />
      {/* Landing (no identity selected) = the 5b meta-profiling landscape: cluster
          cards + profiled-databases table. Selecting a cluster keeps the original
          two-pane list/detail experience at /metadata/:identityId. */}
      {selectedId == null && !error ? (
        <div className='metadata-landing'>
          <MetaLandscape
            identities={identities}
            loading={loading}
            onSelect={handleSelect}
            activeJobs={activeJobs}
            cancelling={cancelling}
            onCancelJob={handleCancelJob}
            onProfileStarted={handleProfileStarted}
          />
        </div>
      ) : (
        <div className='metadata-main'>
          <SchemaIdentityList
            identities={identities}
            sort={sort}
            selectedId={selectedId}
            onSelect={handleSelect}
            loading={loading}
            activeJobs={activeJobs}
            onCancelJob={isReadOnly ? undefined : handleCancelJob}
            cancelling={cancelling}
          />
          {error ? (
            <div className='metadata-right'>
              <div className='mr-header'>
                <span className='mr-name'>Schema Intelligence</span>
              </div>
              <div className='mr-body'>
                <div className='metadata-empty' style={{ color: STATUS.danger.text }}>{error}</div>
              </div>
            </div>
          ) : (
            <SchemaIdentityDetail identity={selectedIdentity} identities={identities} threshold={threshold} />
          )}
        </div>
      )}
      <RunProfileModal
        show={runProfileOpen}
        onHide={() => setRunProfileOpen(false)}
        onSuccess={handleProfileSuccess}
      />
    </div>
  );
};

export default Metadata;
